import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ROLE_COLORS } from './constants';

export function RoleAllocationPanel({ timeline, roleLabels }) {
  const totalWeeks = timeline.total_duration_weeks;
  const weekNums = Array.from({ length: totalWeeks }, (_, i) => i + 1);
  const getCellColor = (fte, assignedFte) => {
    if (!fte || fte === 0) return { bg: 'transparent', text: '#94A3B8' };
    const utilPct = assignedFte > 0 ? (fte / assignedFte) : 0;
    if (utilPct > 1.0) return { bg: '#FEE2E2', text: '#DC2626', border: '#FECACA' };
    if (utilPct > 0.75) return { bg: '#0F2040', text: '#FFFFFF' };
    if (utilPct > 0.4) return { bg: '#1E3A5F', text: '#E2E8F0' };
    if (utilPct > 0.1) return { bg: '#334E6F', text: '#CBD5E1' };
    return { bg: '#4A6580', text: '#CBD5E1' };
  };

  return (
    <>
      <Card data-testid="role-timeline-heatmap">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-[#0F2040]">Role Timeline — FTE Utilization by Week</CardTitle>
          <p className="text-[10px] text-slate-400 mt-0.5">Cell values = FTE required. Red = over assigned capacity.</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] border-separate" style={{ borderSpacing: '2px' }}>
              <thead>
                <tr>
                  <th className="text-left px-2 py-1.5 text-slate-500 font-medium w-36">Role</th>
                  {weekNums.map(w => (<th key={w} className="text-center px-1 py-1.5 text-slate-500 font-medium min-w-[52px]">W{w}</th>))}
                  <th className="text-center px-2 py-1.5 text-slate-500 font-medium min-w-[52px]">Avg Util</th>
                  <th className="text-center px-2 py-1.5 text-slate-500 font-medium min-w-[48px]">Peak</th>
                </tr>
              </thead>
              <tbody>
                {timeline.role_allocation.map((r) => {
                  const wf = r.weekly_fte || {};
                  return (
                    <tr key={r.role}>
                      <td className="px-2 py-1.5 font-medium text-[#0F2040]">
                        <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ backgroundColor: ROLE_COLORS[r.role] }} />
                        {roleLabels[r.role] || r.role}
                        <span className="text-[9px] text-slate-400 ml-1">({r.fte} FTE)</span>
                      </td>
                      {weekNums.map(w => {
                        const fte = wf[w] || 0;
                        const colors = getCellColor(fte, r.fte);
                        return (
                          <td key={w} className="text-center py-1.5 rounded-sm font-mono transition-colors" data-testid={`heatmap-${r.role}-w${w}`}
                            style={{ backgroundColor: colors.bg, color: colors.text, border: colors.border ? `1px solid ${colors.border}` : '1px solid transparent', fontSize: '10px' }}>
                            {fte > 0 ? fte.toFixed(2) : '—'}
                          </td>
                        );
                      })}
                      <td className="text-center py-1.5 font-semibold text-[#0F2040]">{r.avg_utilization ? `${r.avg_utilization}%` : '—'}</td>
                      <td className="text-center py-1.5 text-[10px]">
                        {r.peak_week ? <span className="inline-flex items-center gap-0.5"><span className="font-semibold text-[#0F2040]">W{r.peak_week}</span><span className="text-slate-400">({r.peak_week_fte})</span></span> : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="role-detail-table">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#0F2040]">Role Summary</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[#0F2040] text-white">
                  <th className="text-left px-3 py-2 rounded-tl-md">Role</th>
                  <th className="text-right px-3 py-2">Effort (hrs)</th>
                  <th className="text-right px-3 py-2">FTE Assigned</th>
                  <th className="text-right px-3 py-2">Capacity/Wk</th>
                  <th className="text-left px-3 py-2">Active Weeks</th>
                  <th className="text-right px-3 py-2">Avg Utilization</th>
                  <th className="text-right px-3 py-2 rounded-tr-md">Peak Week</th>
                </tr>
              </thead>
              <tbody>
                {timeline.role_allocation.map((r, i) => (
                  <tr key={r.role} className={i % 2 ? 'bg-slate-50' : ''}>
                    <td className="px-3 py-2 font-medium">
                      <span className="inline-block w-2.5 h-2.5 rounded-full mr-1.5" style={{ backgroundColor: ROLE_COLORS[r.role] }} />
                      {roleLabels[r.role] || r.role}
                    </td>
                    <td className="text-right px-3 py-2">{r.effort_hours.toLocaleString()}</td>
                    <td className="text-right px-3 py-2">{r.fte}</td>
                    <td className="text-right px-3 py-2">{r.capacity_per_week} hrs</td>
                    <td className="text-left px-3 py-2 font-mono text-[11px]">{r.active_week_ranges || '—'}</td>
                    <td className="text-right px-3 py-2">
                      <span className={`font-semibold ${r.avg_utilization > 100 ? 'text-red-600' : r.avg_utilization > 75 ? 'text-[#0F2040]' : 'text-slate-600'}`}>{r.avg_utilization}%</span>
                    </td>
                    <td className="text-right px-3 py-2">
                      {r.peak_week ? <span>W{r.peak_week} <span className="text-slate-400">({r.peak_week_fte} FTE)</span></span> : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
